import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";

import { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";

import { selectCurrentUser } from "../../redux/user/user.selectors";
import { sessionExpired, updateToken, setCurrentUser } from "../../redux/user/user.actions";
import { setCurrentDistro } from "../../redux/distro/distro.actions";
import { selectCurrentDistro } from "../../redux/distro/distro.selector";
import { authFetch, getApiUrl, SessionExpiredError } from "../../utils/api";

import PageWrapper from "../../components/page-wrapper/page-wrapper.components";
import AccountInfoOwner from "../../components/account-info/account-info-owner/account-info-owner.component";
import { SectionWrapper } from "../../components/section-wrapper/sectionWrapper.styles";
import DistroList from "../../components/distros-list/distros-list.component";

import { ownerSidebarData } from "../../components/sidebar/sidebarData";

const OwnerInfoPage = ({ currentUser, currentDistro, setCurrentDistro, onSessionExpired, onTokenRefresh, onSetCurrentUser }) => {
  const [userInfo, setUserInfo] = useState({});
  const [loadingError, setLoadingError] = useState(false);
  const toastIdRef = useRef(null);
  const apiUrl = getApiUrl();
  const authParams = { currentUser, onSessionExpired, onTokenRefresh };

  const fetchData = async (distroId) => {
    toastIdRef.current = toast.loading('Loading owner info...');
    try {
      const response = await authFetch(
        `${apiUrl}/users/${currentUser.id}/${distroId}`,
        {},
        authParams
      );
      if (response.ok) {
        const data = await response.json();
        setUserInfo(data);
        toast.dismiss(toastIdRef.current);
      } else {
        toast.error('Failed to load owner info', { id: toastIdRef.current });
        setLoadingError(true);
        throw new Error(response.statusText);
      }
    } catch (err) {
      toast.dismiss(toastIdRef.current);
      if (!(err instanceof SessionExpiredError)) {
        setLoadingError(true);
      }
    }
  };

  useEffect(() => {
    // guard: ensure currentUser and distros are available
    if (!currentUser?.token || !currentUser?.distros) return;

    if (currentUser.distros.length === 1) {
      setCurrentDistro(currentUser.distros[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  useEffect(() => {
    // guard: ensure token and distro are available
    if (!currentUser?.token || currentDistro === null) return;

    fetchData(currentDistro);
    return () => {
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, currentDistro]);

  const handleClick = (e) => {
    setCurrentDistro(e.target.dataset.id);
  };
  return (
    <PageWrapper sidebarData={ownerSidebarData}>
      {currentDistro === null ? (
        <DistroList distros={currentUser?.distros} handleClick={handleClick} />
      ) : Object.entries(userInfo).length > 0 && !loadingError ? (
        <AccountInfoOwner
          info={userInfo}
          currentUser={currentUser}
          setCurrentUser={onSetCurrentUser}
          onSessionExpired={onSessionExpired}
          onTokenRefresh={onTokenRefresh}
          onProfileUpdated={() => fetchData(currentDistro)}
        />
      ) : loadingError ? (
        <SectionWrapper>
          <h1 className="text-center">
            An error has occured while retrieving data. Please refresh the
            page and try again.
          </h1>
        </SectionWrapper>
      ) : null}
    </PageWrapper>
  );
};
const mapStateToProps = createStructuredSelector({
  currentUser: selectCurrentUser,
  currentDistro: selectCurrentDistro,
});

const mapDispatchToProps = (dispatch) => ({
  setCurrentDistro: (data) => dispatch(setCurrentDistro(data)),
  onSessionExpired: () => dispatch(sessionExpired()),
  onTokenRefresh: (token) => dispatch(updateToken(token)),
  onSetCurrentUser: (user) => dispatch(setCurrentUser(user)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OwnerInfoPage);
